import { createContext, useContext } from "react";

import {
  DateRange,
  DealBase,
  IApproveMeasureFilters,
  IInvestor,
  IRecommendationChild,
  IRecommendationData,
  IRecommendationDataFilters,
  IRecommendationItem,
  RecommendationStatus
} from "store/types";

export interface AiDataFilters {
  deals: DealBase[];
  investors: IInvestor[];
  measures: { id: number; name: string }[];
  effectiveDate: DateRange<string> | null;
  createdAt: DateRange<string> | null;
  status: RecommendationStatus[] | null;
}

export enum RecommendationField {
  investor = "investor",
  measure = "measure",
  value = "value",
  date = "date",
  classification = "classification"
}

export interface AiDataFilters {
  deals: DealBase[];
  investors: IInvestor[];
  measures: { id: number; name: string }[];
  effectiveDate: DateRange<string> | null;
  createdAt: DateRange<string> | null;
  status: RecommendationStatus[] | null;
}

export interface IChangeSpecificRecommendationValue {
  id: number;
  field: string;
  value: string | number | { id?: number | string | null; split_by_commitments?: boolean };
}

export interface IOpenedCell {
  anchorEl: HTMLElement | null;
  data: { parameter: IRecommendationItem; dealId: number; is_global_value?: boolean };
  selectedField: RecommendationField | null;
}

export interface IHandleCellClick {
  event: React.MouseEvent<HTMLTableCellElement | HTMLSpanElement>;
  data: { parameter: IRecommendationItem; dealId: number; is_global_value?: boolean };
  field: RecommendationField;
}

interface IAiDataContext {
  openedCell: IOpenedCell | null;
  setOpenedCell: React.Dispatch<React.SetStateAction<IOpenedCell | null>>;
  handleCellClick: ({ event, data, field }: IHandleCellClick) => void;
  userHasEditPermission: boolean;

  recommendationsTable: IRecommendationData[] | undefined;
  isRecommendationsTableLoading: boolean;
  getTable: (filterPayload: IRecommendationDataFilters) => void;
  setRecommendationsTable: (data: IRecommendationData[]) => void;
  filters: AiDataFilters;
  updateAiDataFilters: (params: Partial<AiDataFilters> | null) => void;
  onGroupRowApprove: (payload: IApproveMeasureFilters) => void;

  expandedRows: Set<string>;
  toggleRowExpansion: (uniqueId: string) => void;

  onChangeRecommendationStatus: (status: RecommendationStatus, id: number) => void;
  onMultiChangeRecommendationStatus: () => void;
  isRecommendationFilled: (recommendation: IRecommendationItem) => boolean;
  getMissingRequiredFields: (recommendation: IRecommendationItem) => string[];
  isRecommendationWithChildrenFilled: (recommendation: IRecommendationChild) => boolean;
  onChangeSpecificRecommendationValue: (data: IChangeSpecificRecommendationValue) => void;
  regenerateRecommendationData: (fileId: number) => void;

  // Hierarchical checkbox functions
  selectedUiIds: Set<string>;
  selectedParameterIds: number[];
  someSelectedRecommendationsAreIncomplete: boolean;
  handleHierarchicalCheckboxChange: (
    uniqueId: string,
    checked: boolean,
    item?: IRecommendationChild | IRecommendationData,
    fileId?: number
  ) => void;
  isUniqueIdSelected: (uniqueId: string, item?: IRecommendationChild | IRecommendationData, fileId?: number) => boolean;
  isUniqueIdPartiallySelected: (item?: IRecommendationChild | IRecommendationData, fileId?: number) => boolean;
  clearAllSelections: () => void;
  generateUniqueId: (item: IRecommendationChild | IRecommendationData, fileId: number) => string;

  // Pagination
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  } | null;
  handleChangePage: (newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;

  allChildAccepted: (item: IRecommendationChild) => boolean;
}

export const AiDataContext = createContext<IAiDataContext>({} as IAiDataContext);

export const useAiDataContext = (): IAiDataContext => useContext(AiDataContext);
